/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useCallback, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {defineMessage, useIntl, FormattedMessage} from 'react-intl'
import {useForm} from 'react-hook-form'
import {useLocation} from 'react-router-dom'
import {
    Box,
    Text,
    useBreakpointValue,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCustomer, useCustomerId, useCustomerType} from '@salesforce/commerce-sdk-react'
import {useCustomToast} from '../contexts/custom-toast-context'
import LoginForm from '../components/login'
import ResetPasswordForm from '../components/reset-password'
import RegisterForm from '../components/register'
import CompleteRegistration from '../components/complete-registration'
import PasswordlessEmailConfirmation from '@salesforce/retail-react-app/app/components/email-confirmation/index'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {LOGIN_TYPES} from '../constants'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {usePrevious} from '@salesforce/retail-react-app/app/hooks/use-previous'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// Import centralized hooks
import {useLoginSubmit} from '../hooks/use-login-submit'
import {useRegistrationSubmit} from '../hooks/use-registration-submit'
import {usePasswordResetSubmit} from '../hooks/use-password-reset-submit'

import SocialLogin from '../components/social-login'
import StandardDrawer from '../components/standard-drawer'
import {Tabs, TabList, TabPanels, Tab, TabPanel, TabIndicator} from '@chakra-ui/react'
import {useBrand} from '../contexts/brand-context'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {usePhoneValidation, useEmailValidation} from '../integrations/melissa/hooks/use-melissa'

export const LOGIN_VIEW = 'login'
export const REGISTER_VIEW = 'register'
export const PASSWORD_VIEW = 'password'
export const EMAIL_VIEW = 'email'
export const EMAIL_CONFIRM_VIEW = 'email-confirm'

const LOGIN_ERROR = defineMessage({
    defaultMessage: "Something's not right with your email or password. Try again.",
    id: 'auth_modal.error.incorrect_email_or_password'
})
    

export const AuthModal = ({
    initialView = LOGIN_VIEW,
    initialEmail = '',
    onLoginSuccess = noop,
    onRegistrationSuccess = noop,
    isOpen,
    onOpen,
    onClose,
    isPasswordlessEnabled = false,
    isSocialEnabled = false,
    idps = [],
    isWishlistClick = false,
    ...props
}) => {
    const {formatMessage} = useIntl()
    const location = useLocation()
    const customerId = useCustomerId()
    const {isRegistered, customerType} = useCustomerType()
    const prevAuthType = usePrevious(customerType)
    const navigate = useNavigation()
    const {isGarage} = useBrand()
    const {site} = useMultiSite()
    const defaultCountry = site?.countryCode

    const {fetchEmailValidation} = useEmailValidation()
    const {fetchPhoneValidation} = usePhoneValidation()

    const customer = useCustomer(
        {parameters: {customerId}},
        {enabled: !!customerId && isRegistered}
    )

    const [currentView, setCurrentView] = useState(initialView)
    const drawerBodyRef = useRef(null)
    const loginForm = useForm()
    const registerForm = useForm()
    const passwordResetForm = useForm()
    const {showCustomToast} = useCustomToast()

    // State for login type and passwordless email - keep original local state
    const [loginType, setLoginType] = useState(LOGIN_TYPES.PASSWORD)
    const [passwordlessLoginEmail, setPasswordlessLoginEmail] = useState(initialEmail)

    // Centralized hooks
    const loginSubmit = useLoginSubmit(loginForm, {
        onLoginSuccess: () => {
            // If the user was trying to access a protected route, redirect them there
            if (location?.state?.from?.pathname) {
                navigate(location.state.from.pathname)
            }
        },
        onPasswordlessSuccess: (email) => {
            setPasswordlessLoginEmail(email)
            setCurrentView(EMAIL_VIEW)
        },
        onAccountNotVerified: () => {
            navigate('/email-confirm')
        },
        onCloseModal: onClose,
        onSetCurrentView: setCurrentView,
        context: 'login'
    })

    const registrationSubmit = useRegistrationSubmit(
        registerForm,
        // onSuccess - will be handled in useEffect when isRegistered changes
        noop,
        // onAccountVerificationRequired
        () => {
            setCurrentView(EMAIL_CONFIRM_VIEW)
        }
    )

    const passwordResetSubmit = usePasswordResetSubmit(
        passwordResetForm,
        // onSuccess
        () => {
            setCurrentView(PASSWORD_VIEW)
        }
    )

    const handleLoginFormSubmit = useCallback(
        async (data) => {
            loginForm.clearErrors()
            if (loginType === LOGIN_TYPES.PASSWORD) {
                return await loginSubmit.submitForm(data, currentView)
            }
            if (loginType === LOGIN_TYPES.PASSWORDLESS) {
                if (loginSubmit?.setLoginType) {
                    loginSubmit.setLoginType(LOGIN_TYPES.PASSWORDLESS)
                }
                setPasswordlessLoginEmail(data.email)
                return await loginSubmit.submitForm({email: data.email}, currentView)
            }
        },
        [loginForm, loginType, loginSubmit, currentView]
    )

    const handleRegisterFormSubmit = useCallback(
        async (data) => {
            registerForm.clearErrors('global')
            const normalizedEmail = data.email || ''
            const normalizedPhone = (data.phone || '').replace(/[^\d]/g, '')

            const resultEmail = await fetchEmailValidation({
                email: normalizedEmail,
                shouldFetch: true
            })
            const resultPhone = await fetchPhoneValidation({
                phoneNumber: normalizedPhone,
                countryCode: defaultCountry,
                shouldFetch: true
            })

            const isValidEmail = resultEmail?.emailValidationResult?.isValidEmail ?? true
            const isValidPhone = resultPhone?.phoneValidationResult?.isValidPhone ?? true

            if (!isValidEmail) {
                registerForm.setError('email', {
                    type: 'manual',
                    message: formatMessage({
                        defaultMessage: 'Invalid email address',
                        id: 'address.email.invalid'
                    })
                })
            }

            if (!isValidPhone) {
                registerForm.setError('phone', {
                    type: 'manual',
                    message: formatMessage({
                        defaultMessage: 'Invalid phone number',
                        id: 'address.phone.invalid'
                    })
                })
            }
            if (!isValidEmail || !isValidPhone) {
                return
            }

            return registrationSubmit.submitForm(data)
        },
        [
            registerForm,
            registrationSubmit,
            fetchEmailValidation,
            fetchPhoneValidation,
            defaultCountry
        ]
    )

    const handlePasswordResetFormSubmit = useCallback(
        async (data) => {
            passwordResetForm.clearErrors()
            return passwordResetSubmit.submitForm(data)
        },
        [passwordResetForm, passwordResetSubmit]
    )

    const handlePasswordlessEmailSubmit = useCallback(async () => {
        loginForm.clearErrors()
        return loginSubmit.submitForm({}, EMAIL_VIEW)
    }, [loginForm, loginSubmit])

    // Reset form and local state when opening the modal
    useEffect(() => {
        if (isOpen) {
            setLoginType(LOGIN_TYPES.PASSWORD)
            setCurrentView(initialView)
            loginForm.reset()
            registerForm.reset()
            passwordResetForm.reset()
            // Also reset the hook's login type
            if (loginSubmit?.setLoginType) {
                loginSubmit.setLoginType(LOGIN_TYPES.PASSWORD)
            }
        }
    }, [isOpen])

    // Auto-focus the first field in each form view
    useEffect(() => {
        const initialFieldByView = {
            [LOGIN_VIEW]: 'email',
            [REGISTER_VIEW]: 'email',
            [PASSWORD_VIEW]: 'email',
            [EMAIL_VIEW]: null
        }

        const forms = {
            [LOGIN_VIEW]: loginForm,
            [REGISTER_VIEW]: registerForm,
            [PASSWORD_VIEW]: passwordResetForm,
            [EMAIL_VIEW]: loginForm
        }
        const initialField = initialFieldByView[currentView]
        if (!initialField) {
            return
        }
        let targetForm = forms[currentView]
        const fieldsRef = targetForm.control?.fieldsRef?.current
        fieldsRef?.[initialField]?.ref?.focus?.()
    }, [currentView, isOpen, loginForm, registerForm, passwordResetForm])

    useEffect(() => {
        const body = drawerBodyRef.current
        if (!body) return undefined
        const stopPropagation = (event) => event.stopPropagation()
        body.addEventListener('touchmove', stopPropagation)
        body.addEventListener('wheel', stopPropagation)
        return () => {
            body.removeEventListener('touchmove', stopPropagation)
            body.removeEventListener('wheel', stopPropagation)
        }
    }, [isOpen])

    useEffect(() => {
        if (initialEmail && loginSubmit?.setPasswordlessLoginEmail) {
            loginSubmit.setPasswordlessLoginEmail(initialEmail)
        }
    }, [initialEmail])

    useEffect(() => {
        // Lets determine if the user has either logged in, or registed.
        const loggingIn = currentView === LOGIN_VIEW
        const registering = currentView === REGISTER_VIEW
        const isNowRegistered = isOpen && isRegistered && (loggingIn || registering)
        // If the customer changed, but it's not because they logged in or registered. Do nothing.
        if (!isNowRegistered) {
            return
        }

        // We are done with the modal.
        onClose()

        // Show a toast only for those registed users returning to the site.
        if (loggingIn) {
            showCustomToast({
                message: `${formatMessage(
                    {
                        defaultMessage: 'Welcome {name},',
                        id: 'auth_modal.info.welcome_user'
                    },
                    {
                        name: customer.data?.firstName || ''
                    }
                )} ${
                    isGarage
                        ? formatMessage({
                              defaultMessage: "You're now signed in.",
                              id: 'auth_modal.description.now_signed_in.garage'
                          })
                        : formatMessage({
                              defaultMessage: "You're now signed in.",
                              id: 'auth_modal.description.now_signed_in.dynamite'
                          })
                }`
            })

            // Execute action to be performed on successful login
            onLoginSuccess()
        }

        if (registering) {
            // Execute action to be performed on successful registration
            onRegistrationSuccess()
        }
    }, [isRegistered])

    useEffect(() => {
        if (location.pathname === '/registration' && isOpen) {
            onClose()
        }
    }, [location.pathname, isOpen, onClose])

    const onBackToSignInClick = () =>
        initialView === PASSWORD_VIEW ? onClose() : setCurrentView(LOGIN_VIEW)

    const getDrawerTitle = () => {
        if (currentView === PASSWORD_VIEW) {
            return (
                <FormattedMessage
                    defaultMessage="Forgot Your Password?"
                    id="reset_password_form.title.forgot_password"
                />
            )
        }
    }

    const isLoginOrRegister = currentView === LOGIN_VIEW || currentView === REGISTER_VIEW

    // Shorter panel on mobile for forgot-password + email flows (Chakra isFullHeight is boolean-only).
    const isCompactAuthDrawer = currentView === PASSWORD_VIEW || currentView === EMAIL_CONFIRM_VIEW
    const drawerIsFullHeight =
        useBreakpointValue({base: !isCompactAuthDrawer, lg: true}, {fallback: 'lg'}) ?? true
    const showDrawerTitle = currentView === PASSWORD_VIEW

    return (
        <StandardDrawer
            data-testid="sf-auth-modal"
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
            headerTitle={showDrawerTitle ? getDrawerTitle() : undefined}
            closeAriaLabel={formatMessage({
                id: 'auth_modal.button.close.assistive_msg',
                defaultMessage: 'Close login form'
            })}
            bodyProps={{
                ref: drawerBodyRef,
                paddingTop: 0,
                overscrollBehavior: 'contain'
            }}
            drawerProps={{
                isFullHeight: drawerIsFullHeight,
                ...props
            }}
        >
            {isLoginOrRegister && (
                <Tabs
                    isLazy
                    variant="unstyled"
                    index={currentView === REGISTER_VIEW ? 1 : 0}
                    onChange={(index) => setCurrentView(index === 0 ? LOGIN_VIEW : REGISTER_VIEW)}
                    data-testid="auth-modal-tabs"
                >
                    <TabList gap={4} mx={{base: 4, lg: 0}} data-testid="auth-modal-tab-list">
                        <Tab
                            px={2}
                            py={1.5}
                            fontSize="sm"
                            lineHeight="18px"
                            data-testid="auth-modal-tab-login"
                        >
                            <FormattedMessage id="auth_login.tab" defaultMessage="Login" />
                        </Tab>
                        <Tab
                            px={2}
                            py={1.5}
                            fontSize="sm"
                            lineHeight="18px"
                            data-testid="auth-modal-tab-register"
                        >
                            <FormattedMessage id="auth_register.tab" defaultMessage="Register" />
                        </Tab>
                    </TabList>
                    <TabIndicator mt="-2px" height="1px" bg="black" />
                    <TabPanels>
                        <TabPanel px={0} py={0}>
                            {isWishlistClick && (
                                <Text
                                    p={4}
                                    color="warningText"
                                    bg="alternativeWarning.100"
                                    variant="accountInfoSmall"
                                    marginTop="51px"
                                >
                                    {isGarage ? (
                                        <FormattedMessage
                                            id="login_wishlist.message.garage"
                                            defaultMessage="To access your Wishlist, please sign in or create an account. It's the best way to save your favorites and enjoy a more personalized experience."
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id="login_wishlist.message.dynamite"
                                            defaultMessage="To access your Wishlist, please sign in or create an account. It's the best way to save your favorites and enjoy a more personalized experience."
                                        />
                                    )}
                                </Text>
                            )}
                            {!loginForm.formState.isSubmitSuccessful && (
                                <LoginForm
                                    form={loginForm}
                                    submitForm={handleLoginFormSubmit}
                                    clickCreateAccount={() => setCurrentView(REGISTER_VIEW)}
                                    onClose={onClose}
                                    handlePasswordlessLoginClick={() => {
                                        if (setLoginType) {
                                            setLoginType(LOGIN_TYPES.PASSWORDLESS)
                                        }
                                    }}
                                    handleForgotPasswordClick={() => {
                                        setCurrentView(PASSWORD_VIEW)
                                    }}
                                    isPasswordlessEnabled={isPasswordlessEnabled}
                                    isSocialEnabled={isSocialEnabled}
                                    idps={idps}
                                    setLoginType={setLoginType}
                                />
                            )}
                            <SocialLogin isSocialEnabled={isSocialEnabled} providers={idps} />
                        </TabPanel>
                        <TabPanel px={0} py={0}>
                            {!registerForm.formState.isSubmitSuccessful && (
                                <RegisterForm
                                    form={registerForm}
                                    submitForm={handleRegisterFormSubmit}
                                    clickSignIn={onBackToSignInClick}
                                    isSocialEnabled={isSocialEnabled}
                                    idps={idps}
                                />
                            )}
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            )}
            {currentView === PASSWORD_VIEW && (
                <ResetPasswordForm
                    form={passwordResetForm}
                    submitForm={handlePasswordResetFormSubmit}
                    clickSignIn={onBackToSignInClick}
                    isAccountDashboard={false}
                />
            )}
            {currentView === EMAIL_VIEW && (
                <PasswordlessEmailConfirmation
                    form={loginForm}
                    submitForm={handlePasswordlessEmailSubmit}
                    email={passwordlessLoginEmail}
                />
            )}
            {currentView === EMAIL_CONFIRM_VIEW && (
                <Box my={'18px'}>
                    <CompleteRegistration onBackToShoppingClick={onClose} />
                </Box>
            )}
        </StandardDrawer>
    )
}

AuthModal.propTypes = {
    initialView: PropTypes.oneOf([LOGIN_VIEW, REGISTER_VIEW, PASSWORD_VIEW, EMAIL_VIEW]),
    initialEmail: PropTypes.string,
    isOpen: PropTypes.bool.isRequired,
    onOpen: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onLoginSuccess: PropTypes.func,
    onRegistrationSuccess: PropTypes.func,
    isPasswordlessEnabled: PropTypes.bool,
    isSocialEnabled: PropTypes.bool,
    idps: PropTypes.arrayOf(PropTypes.string),
    isWishlistClick: PropTypes.bool
}

/**
 *
 * @param {('register'|'login'|'password'|'email')} initialView - the initial view for the modal
 * @returns {Object} - Object props to be spread on to the AuthModal component
 */
export const useAuthModal = (defaultInitialView = LOGIN_VIEW) => {
    const {isOpen, onOpen, onClose} = useDisclosure()
    const {passwordless = {}, social = {}} = getConfig().app.login || {}
    const [isWishlistClick, setIsWishlistClick] = useState(false)
    const [initialView, setInitialView] = useState(defaultInitialView)

    const handleClose = () => {
        setIsWishlistClick(false)
        onClose()
    }

    return {
        initialView,
        setInitialView,
        isOpen,
        onOpen,
        onClose: handleClose,
        isPasswordlessEnabled: !!passwordless?.enabled,
        isSocialEnabled: !!social?.enabled,
        idps: social?.idps,
        isWishlistClick,
        setIsWishlistClick
    }
}
